import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
    Badge,
    Divider,
    Collapse,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import PropTypes from 'prop-types';
import Switch from '@mui/material/Switch';

import { withTranslation } from 'react-i18next';
import {
    DeviceTypeTypeahead,
    OSVersionSelectTypeahead,
    ProjectTypeahead,
    ScreensTypeahead,
    UsersMultiTypeahead
} from '../Typeahead';
import { fetchMobileDevices } from '../../../actions';
import {
    mapMobileDeviceFiltersStateToQuery,
    checkSwitchDisableStatus,
    dateFormatValidation,
    badgesFilter,
    MOBILE_DEVICE_FILTER_FIELD_INIT,
    LOCATION_STATES_ID,
    LOCATION_STATES,
    getMinDate,
    getNextYearDateFormatWithoutTime
} from '../../../utils';
import { updateFilters, applyFilters, resetApplyFilters } from '../../../actions/mobileDevicesFilters';
import LocationTreeModeTypeahead from '../Typeahead/LocationTreeModeTypeahead';
import PersistentStorage from '../../../services/storage';
import DismissedEmployees from '../DismissedEmployees';
import MobileDeviceIdMultiInput from '../Typeahead/MobileDeviceIdMultiInput';
import DateRangePicker from '../DateRangePicker';
import GeneralProgressBar from '../GeneralProgressBar';

const styles = theme => ({
    button: {
        margin: theme.spacing(0.5),
        marginTop: theme.spacing(2),
        flexGrow: 1
    },
    filterForm: {
        padding: theme.spacing(1)
    },
    controlButtons: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-around'
    },
    menu: {
        width: '90%',
        padding: '0, 5px, 0, 5px'
    },
    totalWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '5px',
        width: '90%'
    },
    totalBadgeFilter: {
        zIndex: '0',
        '& .MuiBadge-badge': {
            borderRadius: '50%',
            width: '25px',
            height: '25px',
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.primary.main
        }
    },
    wrapperRow: {
        width: '100%',
        marginTop: '10px',
        backgroundColor: theme.palette.action.hover,
        boxShadow: `0px 0px 5px 3px ${theme.palette.action.hover}`,
    },
    badgeFilter: {
        zIndex: '0',
        top: '-19px',
        right: '12px',
        transformOrigin: '100% 0%',
    },
    marginStates: {
        marginLeft: '15px'
    },
    changeFont: { fontFamily: 'Helvetica, Arial, sans-serif' }
});

const minDate = getMinDate('01/01/1996');
const maxDate = getNextYearDateFormatWithoutTime();

class MobileDeviceFilter extends Component {
    state = {
        assignmentDateFocusedInput: null,
        deliveryDateFocusedInput: null,
        mobileDeviceIdValue: '',
        errors: {
            assignmentDateFrom: null,
            assignmentDateTo: null,
            deliveryDateFrom: null,
            deliveryDateTo: null
        },
        filterMenuState: {
            mobileDeviceMenu: false,
            userMenu: false,
            locationMenu: false,
            dateMenu: false,
            conditionMenu: false,
        },
    };

    componentDidMount() {
        const { i18n } = this.props;
        i18n.loadNamespaces(['mobiledevices']);
        dateFormatValidation('assignmentDateFrom', 'assignmentDateTo');
        dateFormatValidation('deliveryDateFrom', 'deliveryDateTo');
        const filterMenuGroupCondition = PersistentStorage.getItem('mobileDeviceFilterMenuState');
        if (filterMenuGroupCondition) {
            this.setState(state => ({
                ...state,
                filterMenuState: filterMenuGroupCondition,
            }));
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.filterMenuState !== this.state.filterMenuState) {
            PersistentStorage.setItem('mobileDeviceFilterMenuState', this.state.filterMenuState);
        }
    }

    handleResetClick = async () => {
        await this.props.resetApplyFilters();

        const filters = mapMobileDeviceFiltersStateToQuery(this.props.mobileDevicesFilters);

        this.props.fetchMobileDevices(filters);
    };

    handleChangeSearchData = e => {
        this.props.updateFilters({
            ...this.props.mobileDevicesFilters,
            [e.target.name]: e.target.value
        });
    };

    handleChangeMultiSuggestion = name => suggestions => {
        this.props.updateFilters({
            ...this.props.mobileDevicesFilters,
            [name]: suggestions
        });
    };

    handleClickApply = e => {
        e.preventDefault();

        const { assignmentDateFrom, assignmentDateTo, deliveryDateFrom, deliveryDateTo } = this.state.errors;

        const isError = assignmentDateFrom || assignmentDateTo || deliveryDateFrom || deliveryDateTo;

        if (!isError) {
            const filters = mapMobileDeviceFiltersStateToQuery(this.props.mobileDevicesFilters);
            this.props.applyFilters(this.props.mobileDevicesFilters);
            this.props.fetchMobileDevices(filters);
        }
    };

    handleChangeDate = name => date => {
        this.props.updateFilters({
            ...this.props.mobileDevicesFilters,
            [name]: date,
        });
    };

    handleChangeCondition = name => e => {
        this.props.updateFilters({
            ...this.props.mobileDevicesFilters,
            condition: {
                ...this.props.mobileDevicesFilters.condition,
                [name]: e.target.checked
            }
        });
    };

    handleChangeStatusOffices = name => e => {
        this.props.updateFilters({
            ...this.props.mobileDevicesFilters,
            [name]: e.target.checked ? LOCATION_STATES.all : LOCATION_STATES.onlyActive
        });
    };

    handleChangeState = name => e => {
        this.props.updateFilters({
            ...this.props.mobileDevicesFilters,
            state: {
                ...this.props.mobileDevicesFilters.state,
                [name]: e.target.checked
            }
        });
    };

    handleChangeRangeType = (name, status) => {
        this.props.updateFilters({
            ...this.props.mobileDevicesFilters,
            rangeOSVersion: status ? {
                ...this.props.mobileDevicesFilters.rangeOSVersion,
                [name]: status
            } : { isRanged: false, osVersion: null }
        });
    }

    handleChangeGroupFields = name => {
        this.setState(state => ({ ...state,
            filterMenuState: { ...state.filterMenuState, [name]: !this.state.filterMenuState[name] } }));
    }

    getNumberOfSelectedFilters = (filtersAll, initObj) => Object.entries(filtersAll).reduce((acc, arr) => {

        Object.keys(initObj).forEach(el => badgesFilter(initObj[el], arr, acc, el));
        return acc;

    }, { mobileDeviceFilter: 0, userFilter: 0, locationFilter: 0, dateFilter: 0, conditionFilter: 0, total: 0 });

    updateFilterDismissedEmployees = e => {
        const value = e.target.checked;
        this.props.updateFilters({
            ...this.props.mobileDevicesFilters,
            ...this.props.mobileDevicesFilters.state,
            isDismissedEmployees: value || null,
            users: [],
            projects: [],
        });
    }

    handleChangeMobileDeviceIdValue = value => {
        this.setState({ mobileDeviceIdValue: value });
    }

    handleAddIdMultiInput = value => {
        this.props.updateFilters({
            ...this.props.mobileDevicesFilters,
            ids: [...this.props.mobileDevicesFilters.ids, { value }],
        });
        this.setState({ mobileDeviceIdValue: '' });
    }

    handleDeleteItemIdMultiInput = values => {
        this.props.updateFilters({
            ...this.props.mobileDevicesFilters,
            ids: values,
        });
    }

    handleErrorDate = (error, name) => {
        this.setState(state => ({
            ...state,
            errors: {
                ...state.errors,
                [name]: error
            }
        }));
    }

    render() {
        const { classes, mobileDevicesFilters, osVersions, offices, t } = this.props;
        const { isDismissedEmployees, officeState, resourceOwnerState } = mobileDevicesFilters;
        const { filterMenuState: { mobileDeviceMenu, userMenu, locationMenu, dateMenu, conditionMenu },
            mobileDeviceIdValue, errors } = this.state;
        const { condition: deviceConditions, state: deviceStates } = mobileDevicesFilters;
        const { working, nonWorking, workingConditionally } = deviceConditions;
        const { inUse, onStock, underRepair } = deviceStates;
        const {
            mobileDeviceFilter,
            userFilter,
            locationFilter,
            dateFilter,
            conditionFilter,
        } = this.getNumberOfSelectedFilters(this.props.mobileDevicesFilters, MOBILE_DEVICE_FILTER_FIELD_INIT);
        const { total } =
            this.getNumberOfSelectedFilters(this.props.appliedMobileDevicesFilters, MOBILE_DEVICE_FILTER_FIELD_INIT);

        const isOfficeState = officeState === LOCATION_STATES_ID.all.id;
        const isResourceOwnerState = resourceOwnerState === LOCATION_STATES_ID.all.id;

        return offices ? (
            <form
                className={classNames('filter', classes.filterForm)}
                autoComplete="off"
                onSubmit={this.handleClickApply}
            >
                <div className={classNames(classes.totalWrapper)}>
                    <span>
                        {t('totalFilterFieldsSelected')}
                        :
                    </span>
                    {!!total && (
                        <Badge
                            className={classNames(classes.totalBadgeFilter)}
                            badgeContent={total}
                            overlap="rectangular"
                            invisible={!total}
                        />
                    )}
                </div>
                <FormControl fullWidth>
                    <InputLabel
                        htmlFor="searchData"
                        variant="standard"
                    >
                        {t('search')}
                    </InputLabel>
                    <Input
                        className={classes.changeFont}
                        id="searchData"
                        name="searchData"
                        value={mobileDevicesFilters.searchData}
                        onChange={this.handleChangeSearchData}
                    />
                </FormControl>

                <FormControl className={classNames(classes.wrapperRow)}>
                    <FormControlLabel
                        className={classNames(classes.menu)}
                        control={(
                            <Switch
                                checked={mobileDeviceMenu}
                                onChange={() => this.handleChangeGroupFields('mobileDeviceMenu')}
                                color="secondary"
                            />
                        )}
                        label={t('mobileDevice')}
                    />

                    <Badge
                        className={classNames(classes.badgeFilter)}
                        badgeContent={mobileDeviceFilter}
                        color="secondary"
                        overlap="rectangular"
                        invisible={!(mobileDeviceFilter && !mobileDeviceMenu)}
                    />
                </FormControl>

                <Divider />
                <Collapse in={mobileDeviceMenu}>
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <MobileDeviceIdMultiInput
                            value={mobileDeviceIdValue}
                            onChangeValue={this.handleChangeMobileDeviceIdValue}
                            values={mobileDevicesFilters.ids}
                            onChangeMultiInput={this.handleAddIdMultiInput}
                            onDeleteValue={this.handleDeleteItemIdMultiInput}
                            t={t}
                        />
                    </FormControl>
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <DeviceTypeTypeahead
                            isMulti
                            animatedPlaceholder
                            typeOfSearch="mobile"
                            value={mobileDevicesFilters.deviceTypes}
                            onChangeSuggestion={this.handleChangeMultiSuggestion('deviceTypes')}
                            t={t}
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <OSVersionSelectTypeahead
                            rangeOSVersion={mobileDevicesFilters.rangeOSVersion}
                            handleChangeRangeType={this.handleChangeRangeType}
                            osVersions={osVersions}
                            versionsList={mobileDevicesFilters.osVersions || []}
                            onChangeSuggestion={this.handleChangeMultiSuggestion('osVersions')}
                            t={t}
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <ScreensTypeahead
                            isMulti
                            animatedPlaceholder
                            margin="none"
                            value={mobileDevicesFilters.screens}
                            onChangeSuggestion={this.handleChangeMultiSuggestion('screens')}
                            t={t}
                        />
                    </FormControl>
                </Collapse>

                <FormControl className={classNames(classes.wrapperRow)}>
                    <FormControlLabel
                        className={classNames(classes.menu)}
                        control={(
                            <Switch
                                checked={userMenu}
                                onChange={() => this.handleChangeGroupFields('userMenu')}
                                color="secondary"
                            />
                        )}
                        label={t('userProfile')}
                    />

                    <Badge
                        className={classNames(classes.badgeFilter)}
                        badgeContent={userFilter}
                        color="secondary"
                        overlap="rectangular"
                        invisible={!(userFilter && !userMenu)}
                    />
                </FormControl>

                <Divider />
                <Collapse in={userMenu}>
                    <DismissedEmployees
                        setDismissedEmployees={this.updateFilterDismissedEmployees}
                        dismissedEmployees={isDismissedEmployees}
                        classes={classes}
                        t={t}
                    />
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <UsersMultiTypeahead
                            isMulti
                            animatedPlaceholder
                            value={mobileDevicesFilters.users}
                            labelId="user"
                            onChangeSuggestion={this.handleChangeMultiSuggestion('users')}
                            isDismissedEmployees={isDismissedEmployees}
                            t={t}
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        margin="dense"
                        sx={{ display: 'none' }}
                    >
                        <ProjectTypeahead
                            isMulti
                            animatedPlaceholder
                            value={mobileDevicesFilters.projects}
                            labelId="project"
                            onChangeSuggestion={this.handleChangeMultiSuggestion('projects')}
                            disabled={isDismissedEmployees}
                            t={t}
                        />
                    </FormControl>
                </Collapse>

                <FormControl className={classNames(classes.wrapperRow)}>
                    <FormControlLabel
                        className={classNames(classes.menu)}
                        control={(
                            <Switch
                                checked={locationMenu}
                                onChange={() => this.handleChangeGroupFields('locationMenu')}
                                color="secondary"
                            />
                        )}
                        label={t('office')}
                    />

                    <Badge
                        className={classNames(classes.badgeFilter)}
                        badgeContent={locationFilter}
                        color="secondary"
                        overlap="rectangular"
                        invisible={!(locationFilter && !locationMenu)}
                    />
                </FormControl>

                <Divider />
                <Collapse in={locationMenu}>
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    checked={isOfficeState}
                                    onChange={this.handleChangeStatusOffices('officeState')}
                                />
                            )}
                            label={t('active/All')}
                        />
                        <LocationTreeModeTypeahead
                            data={[isOfficeState ? offices.all : offices.onlyActive]}
                            placeholder="office"
                            locationId={mobileDevicesFilters.offices.map(location => location.value)}
                            strict={mobileDevicesFilters.strict}
                            setLocation={value =>
                                this.handleChangeMultiSuggestion('offices')(value.map(item => ({ value: item })))}
                            setStrict={value =>
                                this.handleChangeMultiSuggestion('strict')(value)}
                            hierarchical
                            multi
                            descendants
                        />
                    </FormControl>

                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <FormControlLabel
                            className={classes.marginStates}
                            control={(
                                <Switch
                                    checked={isResourceOwnerState}
                                    onChange={this.handleChangeStatusOffices('resourceOwnerState')}
                                />
                            )}
                            label={t('active/All')}
                        />
                        <LocationTreeModeTypeahead
                            data={[isResourceOwnerState ? offices.all : offices.onlyActive]}
                            placeholder="resourceOwner"
                            locationId={mobileDevicesFilters.resourceOwner.map(location => location.value)}
                            strict={mobileDevicesFilters.resourceOwnerStrict}
                            setLocation={value =>
                                this.handleChangeMultiSuggestion('resourceOwner')(value.map(item => ({ value: item })))}
                            setStrict={value =>
                                this.handleChangeMultiSuggestion('resourceOwnerStrict')(value)}
                            hierarchical
                            multi
                            descendants
                        />
                    </FormControl>
                </Collapse>

                <FormControl className={classNames(classes.wrapperRow)}>
                    <FormControlLabel
                        className={classNames(classes.menu)}
                        control={(
                            <Switch
                                checked={dateMenu}
                                onChange={() => this.handleChangeGroupFields('dateMenu')}
                                color="secondary"
                            />
                        )}
                        label={t('date')}
                    />

                    <Badge
                        className={classNames(classes.badgeFilter)}
                        badgeContent={dateFilter}
                        color="secondary"
                        overlap="rectangular"
                        invisible={!(dateFilter && !dateMenu)}
                    />
                </FormControl>

                <Divider />
                <Collapse in={dateMenu}>
                    <DateRangePicker
                        isColumn
                        startName="assignmentDateFrom"
                        endName="assignmentDateTo"
                        title="assignmentDate"
                        minDate={minDate}
                        maxDate={maxDate}
                        value={{
                            startDate: mobileDevicesFilters.assignmentDateFrom,
                            endDate: mobileDevicesFilters.assignmentDateTo
                        }}
                        handleChangeStartDate={this.handleChangeDate}
                        handleChangeEndDate={this.handleChangeDate}
                        handleError={this.handleErrorDate}
                        t={t}
                        errors={errors}
                    />

                    <DateRangePicker
                        isColumn
                        startName="deliveryDateFrom"
                        endName="deliveryDateTo"
                        title="deliveryDate"
                        minDate={minDate}
                        maxDate={maxDate}
                        value={{
                            startDate: mobileDevicesFilters.deliveryDateFrom,
                            endDate: mobileDevicesFilters.deliveryDateTo
                        }}
                        handleChangeStartDate={this.handleChangeDate}
                        handleChangeEndDate={this.handleChangeDate}
                        handleError={this.handleErrorDate}
                        t={t}
                        errors={errors}
                    />
                </Collapse>

                <FormControl className={classNames(classes.wrapperRow)}>
                    <FormControlLabel
                        className={classNames(classes.menu)}
                        control={(
                            <Switch
                                checked={conditionMenu}
                                onChange={() => this.handleChangeGroupFields('conditionMenu')}
                                color="secondary"
                            />
                        )}
                        label={t('deviceState')}
                    />

                    <Badge
                        className={classNames(classes.badgeFilter)}
                        badgeContent={conditionFilter}
                        color="secondary"
                        overlap="rectangular"
                        invisible={!(conditionFilter && !conditionMenu)}
                    />
                </FormControl>

                <Divider />
                <Collapse in={conditionMenu}>
                    <FormControl
                        component="fieldset"
                        margin="dense"
                    >
                        <FormLabel component="legend">
                            {t('condition')}
                        </FormLabel>
                        <FormControlLabel
                            control={(
                                <Switch
                                    className={classes.marginStates}
                                    disabled={checkSwitchDisableStatus(working, deviceConditions)}
                                    checked={working}
                                    onChange={this.handleChangeCondition('working')}
                                />
                            )}
                            label={t('working')}
                        />
                        <FormControlLabel
                            control={(
                                <Switch
                                    className={classes.marginStates}
                                    disabled={checkSwitchDisableStatus(nonWorking, deviceConditions)}
                                    checked={nonWorking}
                                    onChange={this.handleChangeCondition('nonWorking')}
                                />
                            )}
                            label={t('nonWorking')}
                        />
                        <FormControlLabel
                            control={(
                                <Switch
                                    className={classes.marginStates}
                                    disabled={checkSwitchDisableStatus(workingConditionally, deviceConditions)}
                                    checked={workingConditionally}
                                    onChange={this.handleChangeCondition('workingConditionally')}
                                />
                            )}
                            label={t('workingConditionally')}
                        />
                    </FormControl>

                    <FormControl
                        component="fieldset"
                        margin="dense"
                    >
                        <FormLabel component="legend">
                            {t('state')}
                        </FormLabel>
                        <FormControlLabel
                            control={(
                                <Switch
                                    className={classes.marginStates}
                                    disabled={checkSwitchDisableStatus(inUse, deviceStates)}
                                    checked={inUse}
                                    onChange={this.handleChangeState('inUse')}
                                />
                            )}
                            label={t('inUse')}
                        />
                        <FormControlLabel
                            control={(
                                <Switch
                                    className={classes.marginStates}
                                    disabled={checkSwitchDisableStatus(onStock, deviceStates)}
                                    checked={onStock}
                                    onChange={this.handleChangeState('onStock')}
                                />
                            )}
                            label={t('onStock')}
                        />
                        <FormControlLabel
                            control={(
                                <Switch
                                    className={classes.marginStates}
                                    disabled={checkSwitchDisableStatus(underRepair, deviceStates)}
                                    checked={underRepair}
                                    onChange={this.handleChangeState('underRepair')}
                                />
                            )}
                            label={t('underRepair')}
                        />
                    </FormControl>
                </Collapse>

                <div className={classes.controlButtons}>
                    <Button
                        variant="contained"
                        className={classes.button}
                        onClick={this.handleResetClick}
                    >
                        {t('reset')}
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.button}
                        color="secondary"
                        type="submit"
                    >
                        {t('apply')}
                    </Button>
                </div>
            </form>
        ) : (<GeneralProgressBar />);
    }
}

MobileDeviceFilter.propTypes = {
    mobileDevicesFilters: PropTypes.shape({
        rangeOSVersion: PropTypes.object,
        condition: PropTypes.shape({
            working: PropTypes.bool,
            nonWorking: PropTypes.bool,
            workingConditionally: PropTypes.bool,
        }),
        state: PropTypes.shape({
            inUse: PropTypes.bool,
            onStock: PropTypes.bool,
            underRepair: PropTypes.bool,
        }),
        offices: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.number,
            }),
        ),
        deviceTypes: PropTypes.array,
        searchData: PropTypes.string,
        osVersions: PropTypes.array,
        screens: PropTypes.array,
        users: PropTypes.array,
        projects: PropTypes.array,
        assignmentDateFrom: PropTypes.object,
        assignmentDateTo: PropTypes.object,
        deliveryDateFrom: PropTypes.object,
        deliveryDateTo: PropTypes.object,
    }),
    offices: PropTypes.object,
    strict: PropTypes.bool,
    officeState: PropTypes.number,
    resourceOwnerState: PropTypes.number,
    osVersions: PropTypes.array,
    classes: PropTypes.shape({
        filterForm: PropTypes.string,
        controlButtons: PropTypes.string,
        button: PropTypes.string,
    }),
    fetchMobileDevices: PropTypes.func,
    updateFilters: PropTypes.func,
    applyFilters: PropTypes.func,
    resetApplyFilters: PropTypes.func,
};

const mapStateToProps = ({ mobileDevicesFilters, infrastructure }) => ({
    mobileDevicesFilters: mobileDevicesFilters.currentFilters,
    appliedMobileDevicesFilters: mobileDevicesFilters.appliedFilters,
    offices: infrastructure.locations[0],
    osVersions: infrastructure.osVersions
});

export default compose(
    withTranslation('mobiledevices'),
    connect(mapStateToProps, { fetchMobileDevices, updateFilters, applyFilters, resetApplyFilters }),
    withStyles(styles)
)(MobileDeviceFilter);
